import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  Button,
  IconButton,
  Alert,
  Tooltip,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import {
  Schedule,
  CheckCircle,
  Error,
  Refresh,
  Cancel,
  Info,
  Timer,
  PlayArrow
} from '@mui/icons-material';
import { green, orange, red, blue } from '@mui/material/colors';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import toast from 'react-hot-toast';
import postsAPI from '../../services/postsAPI';
import timezoneAPI from '../../services/timezoneAPI';
import { formatTimeWithTimezone } from '../../utils/timezone';

dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(utc);

interface ScheduledPostInfo {
  post_id: string;
  scheduled_time: string;
  time_remaining_seconds: number;
  time_remaining_formatted: string;
  is_scheduled_in_celery: boolean;
  celery_task_id?: string;
  platforms: string[];
}

interface Post {
  id: string;
  topic: string;
  content: string;
  scheduled_time?: string;
  time_remaining_seconds?: number;
  time_remaining_formatted?: string;
  is_scheduled_in_celery?: boolean;
  celery_task_id?: string;
}

interface ScheduledPostsMonitorProps {
  refreshInterval?: number; // in milliseconds, default 30 seconds
}

const ScheduledPostsMonitor: React.FC<ScheduledPostsMonitorProps> = ({ 
  refreshInterval = 30000 
}) => {
  const [scheduledPosts, setScheduledPosts] = useState<ScheduledPostInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPost, setSelectedPost] = useState<ScheduledPostInfo | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancellingPost, setCancellingPost] = useState<string | null>(null);
  const [userTimezone, setUserTimezone] = useState<string>('Asia/Kolkata');

  // Real-time countdown state
  const [currentTime, setCurrentTime] = useState(dayjs());

  const fetchUserTimezone = async () => {
    try {
      const data = await timezoneAPI.getCurrentUserTimezone();
      setUserTimezone(data.user_timezone);
    } catch (error) {
      console.error('Error fetching user timezone:', error);
      // Keep default timezone
    }
  };

  const fetchScheduledPosts = async () => {
    try {
      setError(null);
      const response = await postsAPI.getScheduledPostsInfo();
      setScheduledPosts(response.data || []);
    } catch (error: any) {
      console.error('Error fetching scheduled posts:', error);
      setError('Failed to fetch scheduled posts');
    } finally {
      setLoading(false);
    }
  };

  // Update current time every second for real-time countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(dayjs());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Fetch scheduled posts on mount and set up refresh interval
  useEffect(() => {
    fetchUserTimezone();
    fetchScheduledPosts();

    const refreshTimer = setInterval(fetchScheduledPosts, refreshInterval);
    return () => clearInterval(refreshTimer);
  }, [refreshInterval]);

  const calculateRealTimeRemaining = (scheduledTime: string) => {
    // Parse the UTC time and ensure it's treated as UTC
    const scheduled = dayjs.utc(scheduledTime);
    const now = dayjs.utc();
    const diff = scheduled.diff(now);
    
    if (diff <= 0) {
      return { seconds: 0, formatted: 'Overdue', percentage: 100 };
    }

    const duration = dayjs.duration(diff);
    const days = Math.floor(duration.asDays());
    const hours = duration.hours();
    const minutes = duration.minutes();
    const seconds = duration.seconds();

    let formatted = '';
    if (days > 0) formatted += `${days}d `;
    if (hours > 0) formatted += `${hours}h `;
    if (minutes > 0) formatted += `${minutes}m `;
    if (seconds > 0 && days === 0) formatted += `${seconds}s`;

    // Calculate percentage for progress bar (assuming 24 hours max for visualization)
    const totalSeconds = Math.min(24 * 60 * 60, Math.floor(diff / 1000));
    const maxSeconds = 24 * 60 * 60;
    const percentage = Math.max(0, ((maxSeconds - totalSeconds) / maxSeconds) * 100);

    return { 
      seconds: Math.floor(diff / 1000), 
      formatted: formatted.trim() || 'Less than 1s',
      percentage 
    };
  };

  const getStatusColor = (post: ScheduledPostInfo) => {
    if (!post.is_scheduled_in_celery) return red[500];
    
    const remaining = calculateRealTimeRemaining(post.scheduled_time);
    if (remaining.seconds <= 0) return red[500];
    if (remaining.seconds <= 300) return orange[500]; // 5 minutes
    return green[500];
  };

  const handleCancelSchedule = async (postId: string) => {
    setCancellingPost(postId);
    try {
      await postsAPI.cancelSchedule(postId);
      toast.success('Post schedule cancelled successfully');
      fetchScheduledPosts(); // Refresh the list
      setCancelDialogOpen(false);
    } catch (error: any) {
      console.error('Error cancelling schedule:', error);
      toast.error('Failed to cancel schedule');
    } finally {
      setCancellingPost(null);
    }
  };

  const handleResync = async () => {
    setLoading(true);
    try {
      const result = await postsAPI.resyncScheduler();
      toast.success(`Resync successful! ${result.data.job_count} jobs scheduled`);
      fetchScheduledPosts(); // Refresh the list
    } catch (error: any) {
      console.error('Error during resync:', error);
      toast.error(`Resync failed: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (post: ScheduledPostInfo) => {
    setSelectedPost(post);
    setDetailsOpen(true);
  };

  if (loading && scheduledPosts.length === 0) {
    return (
      <Card>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Schedule color="primary" />
            Scheduled Posts Monitor
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            Times shown in {userTimezone.replace('_', ' ')} timezone
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<Refresh />}
            onClick={fetchScheduledPosts}
            disabled={loading}
            size="small"
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            onClick={handleResync}
            disabled={loading}
            size="small"
            color="warning"
          >
            Resync
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {scheduledPosts.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Schedule sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Scheduled Posts
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create and schedule a post to see it here with real-time monitoring
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
          {scheduledPosts.map((post, index) => {
            const remaining = calculateRealTimeRemaining(post.scheduled_time);
            const statusColor = getStatusColor(post);
            
            return (
              <React.Fragment key={post.post_id}>
                <ListItem
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: 'background.default'
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle1" component="span">
                          Post #{post.post_id.slice(-6)}
                        </Typography>
                        <Chip
                          icon={post.is_scheduled_in_celery ? <CheckCircle /> : <Error />}
                          label={post.is_scheduled_in_celery ? 'Scheduled' : 'Not Scheduled'}
                          color={post.is_scheduled_in_celery ? 'success' : 'error'}
                          size="small"
                        />
                        {post.platforms.map(platform => (
                          <Chip 
                            key={platform}
                            label={platform}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Scheduled: {formatTimeWithTimezone(post.scheduled_time, userTimezone)}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Timer sx={{ fontSize: 16, color: statusColor }} />
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: statusColor,
                              fontWeight: 'medium',
                              fontFamily: 'monospace'
                            }}
                          >
                            {remaining.formatted}
                          </Typography>
                        </Box>

                        {remaining.seconds > 0 && (
                          <LinearProgress
                            variant="determinate"
                            value={remaining.percentage}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: 'grey.200',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: statusColor
                              }
                            }}
                          />
                        )}
                      </Box>
                    }
                  />
                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="View Details">
                      <IconButton 
                        size="small" 
                        onClick={() => handleViewDetails(post)}
                      >
                        <Info />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Cancel Schedule">
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => {
                          setSelectedPost(post);
                          setCancelDialogOpen(true);
                        }}
                        disabled={cancellingPost === post.post_id}
                      >
                        {cancellingPost === post.post_id ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Cancel />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItem>
                {index < scheduledPosts.length - 1 && <Divider />}
              </React.Fragment>
            );
          })}
        </List>
      )}

      {/* Details Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={() => setDetailsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Scheduled Post Details
        </DialogTitle>
        <DialogContent>
          {selectedPost && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Post ID: {selectedPost.post_id}
              </Typography>
              
              <Typography variant="subtitle2" gutterBottom>
                Scheduled Time: {formatTimeWithTimezone(selectedPost.scheduled_time, userTimezone)}
              </Typography>
              
              <Typography variant="subtitle2" gutterBottom>
                Platforms: {selectedPost.platforms.join(', ')}
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Celery Status:
                </Typography>
                <Chip
                  icon={selectedPost.is_scheduled_in_celery ? <CheckCircle /> : <Error />}
                  label={selectedPost.is_scheduled_in_celery ? 'Active in Scheduler' : 'Not Found in Scheduler'}
                  color={selectedPost.is_scheduled_in_celery ? 'success' : 'error'}
                />
              </Box>
              
              {selectedPost.celery_task_id && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Task ID:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                    {selectedPost.celery_task_id}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
      >
        <DialogTitle>Cancel Schedule</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel the schedule for this post? 
            The post will be moved back to draft status.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setCancelDialogOpen(false)}
            disabled={cancellingPost !== null}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => selectedPost && handleCancelSchedule(selectedPost.post_id)}
            color="error"
            disabled={cancellingPost !== null}
            startIcon={cancellingPost ? <CircularProgress size={16} /> : <Cancel />}
          >
            {cancellingPost ? 'Cancelling...' : 'Confirm Cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScheduledPostsMonitor; 